// src/pages/ProfileSettingsPage.js (or wherever it lives in your structure)
import React, { useState, useEffect } from 'react';
import { getResearcherProfile, saveResearcherProfile } from '../services/api';
import ChangePassword from '../components/auth/ChangePassword';
import { apiCall } from '../utils/api';
import { validateWebsiteUrl } from '../utils/validation';

const getUserProfile = async (userId) => {
  try {
    const response = await apiCall(`/api/auth/user-profile?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch profile');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

const ProfileSettingsPage = ({ user }) => {  // ✅ Receives user as PROP (your original pattern)
  // User profile state
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Researcher profile state
  const [researcherProfile, setResearcherProfile] = useState({
    company_name: '',
    company_website: '',
    business_description: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [websiteError, setWebsiteError] = useState(null);
  // Load user profile on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) {
        setError('No user data available. Please log in again.');
        setLoading(false);
        return;
      }

      const userId = user.uid || user.google_uid || user.email;
      
      if (!userId) {
        setError('Invalid user data. Please log in again.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const userData = await getUserProfile(userId);
        setProfileData(userData);
        
      } catch (err) {
        console.error('Failed to fetch profile data:', err);
        setError(err.message || 'Could not load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [user]);

  // Load researcher profile on mount
  useEffect(() => {
    const loadResearcherProfile = async () => {
      try {
        const profile = await getResearcherProfile();
        setResearcherProfile({
          company_name: profile.company_name || '',
          company_website: profile.company_website || '',
          business_description: profile.business_description || ''
        });
      } catch (error) {
        console.error('Error loading researcher profile:', error);
      }
    };
    
    loadResearcherProfile();
  }, []);

  const handleResearcherProfileChange = (e) => {
    const { name, value } = e.target;
    setResearcherProfile(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user types
    if (name === 'company_website') {
      setWebsiteError(null);
    }
  };

  const handleWebsiteBlur = () => {
    const validation = validateWebsiteUrl(researcherProfile.company_website);
    if (!validation.isValid) {
      setWebsiteError(validation.error);
    } else {
      setWebsiteError(null);
      // Auto-normalize the URL
      if (validation.normalized !== researcherProfile.company_website) {
        setResearcherProfile(prev => ({
          ...prev,
          company_website: validation.normalized
        }));
      }
    }
  };

  const handleSaveResearcherProfile = async (e) => {
  e.preventDefault();
  setIsSaving(true);
  setSaveMessage(null);
  setSaveError(null);

  try {
    const hasName = researcherProfile.company_name.trim() !== '';
    const hasWebsite = researcherProfile.company_website.trim() !== '';
    const hasDescription = researcherProfile.business_description.trim() !== '';
    
    // Validation: if business description is filled, require name and website
    if (hasDescription && (!hasName || !hasWebsite)) {
      setSaveError('Company name and website are required when providing business description');
      setIsSaving(false);
      return;
    }
    
    // Validation: both name and website must be filled or both empty
    if (hasName !== hasWebsite) {
      setSaveError('Company name and website must both be filled or both left empty');
      setIsSaving(false);
      return;
    }
    
    // Validate website format if provided
    if (hasWebsite) {
      const validation = validateWebsiteUrl(researcherProfile.company_website);
      if (!validation.isValid) {
        setWebsiteError(validation.error);
        setSaveError('Please fix the website URL format before saving');
        setIsSaving(false);
        return;
      }
    }

    await saveResearcherProfile(researcherProfile);
    setSaveMessage('Profile saved successfully!');
    
    // Clear message after 3 seconds
    setTimeout(() => setSaveMessage(null), 3000);
  } catch (error) {
    console.error('Error saving researcher profile:', error);
    setSaveError(error.message || 'Failed to save profile');
  } finally {
    setIsSaving(false);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0c1526] to-[#142039] text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-6">Profile</h1>
          
          {/* User Profile Section */}
          {loading ? (
            <div className="bg-[#1e293b] rounded-xl shadow-xl p-6 border border-gray-700 mb-6 flex justify-center">
              <div className="relative w-12 h-12">
                <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-4 border-gray-700"></div>
                <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-4 border-transparent border-t-[#ff7e00] animate-spin"></div>
              </div>
            </div>
          ) : error ? (
            <div className="bg-[#1e293b] rounded-xl shadow-xl p-6 border border-gray-700 mb-6">
              <div className="text-red-400 mb-3">{error}</div>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="bg-[#1e293b] rounded-xl shadow-xl p-6 border border-gray-700 mb-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Display Name
                  </label>
                  <div className="px-3 py-2 bg-[#273349] border border-gray-600 rounded-md text-white">
                    {profileData?.displayName || user?.displayName || user?.name || 'Not set'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Email
                  </label>
                  <div className="px-3 py-2 bg-[#273349] border border-gray-600 rounded-md text-white">
                    {profileData?.email || user?.email || 'Not available'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    User ID
                  </label>
                  <div className="px-3 py-2 bg-[#273349] border border-gray-600 rounded-md text-white text-sm font-mono">
                    {user?.uid || user?.id || 'Not available'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NEW: Researcher Profile Section */}
          <div className="bg-[#1e293b] rounded-xl shadow-xl p-6 border border-gray-700 mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">Company Profile</h2>
            <p className="text-gray-400 text-sm mb-6">
              This information helps personalize your research reports. All fields are optional.
            </p>
            
            <form onSubmit={handleSaveResearcherProfile}>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2" htmlFor="company_name">
                    Your Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    id="company_name"
                    value={researcherProfile.company_name}
                    onChange={handleResearcherProfileChange}
                    maxLength="200"
                    className="w-full px-3 py-2 bg-[#273349] border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Acme Corporation"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Your company name will be used for contextual analysis
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2" htmlFor="company_website">
                    Your Company Website
                  </label>
                  <input
                    type="text"
                    name="company_website"
                    id="company_website"
                    value={researcherProfile.company_website}
                    onChange={handleResearcherProfileChange}
                    onBlur={handleWebsiteBlur}
                    maxLength="100"
                    className={`w-full px-3 py-2 bg-[#273349] border rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                      websiteError ? 'border-red-500 focus:ring-red-500' : 'border-gray-600 focus:ring-blue-500'
                    }`}
                    placeholder="e.g., acmecorp.com"
                  />
                  {websiteError ? (
                    <p className="mt-1 text-xs text-red-400">{websiteError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Your website helps us understand your business context
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2" htmlFor="business_description">
                    What You Offer (Optional)
                  </label>
                  <input
                    type="text"
                    name="business_description"
                    id="business_description"
                    value={researcherProfile.business_description}
                    onChange={handleResearcherProfileChange}
                    maxLength="100"
                    className="w-full px-3 py-2 bg-[#273349] border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Cloud migration services, CRM software"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Brief description of your products/services (max 100 characters)
                  </p>
                </div>
              </div>

              {/* Save Messages */}
              {saveMessage && (
                <div className="mb-4 bg-green-500/20 border border-green-500/50 rounded-md p-3">
                  <p className="text-green-300 text-sm">{saveMessage}</p>
                </div>
              )}
              
              {saveError && (
                <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-md p-3">
                  <p className="text-red-300 text-sm">{saveError}</p>
                </div>
              )}

              <button 
                type="submit" 
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : 'Save Company Profile'}
              </button>
            </form>
          </div>
          
          {/* Change Password - Only for email-password users */}
          {user && 
           user.providerData && 
           user.providerData[0]?.providerId === 'password' && 
           !loading && 
           !error && (
            <ChangePassword />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsPage;